// Pantalla Pagos recibidos (`/collections/payments`): la banda de totales del cobrador arriba y el
// explorer de 3 columnas —filtros · tabla · ficha— debajo. Las filas salen de un fixture local:
// todavía no hay endpoint de pagos del cobrador, y el sello de mock lo declara en pantalla.
import { useMemo, useState } from 'react';
import { mxDayOffset } from '@/lib/dateHelpers';
import { MockBoundary } from '@/shared/components/MockBoundary';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { screenFrameClass } from '@/shared/patterns/ScreenFrame';
import { StatsBar } from '@/shared/patterns/StatsBar';
import { buildCollectorPayments } from './fixtures';
import {
    applyPaymentStat,
    computePaymentStats,
    computePaymentTotals,
    filterPayments,
    paymentsStatsItems,
    sortPaymentsByRecency,
} from './logic';
import { emptyPaymentFilters, type PaymentFilters, type PaymentStatKey } from './types';
import { PaymentDetail } from './components/PaymentDetail';
import { PaymentsFiltersPanel } from './components/PaymentsFiltersPanel';
import { PaymentsTable } from './components/PaymentsTable';
import { PaymentsTotalsBand } from './components/PaymentsTotalsBand';

export function PaymentsPage() {
    const today = useMemo(() => mxDayOffset(0), []);
    const rows = useMemo(() => buildCollectorPayments(today), [today]);
    const [filters, setFilters] = useState<PaymentFilters>(emptyPaymentFilters);
    const [stat, setStat] = useState<PaymentStatKey>('all');
    const [pickedPaymentId, setPickedPaymentId] = useState<string | null>(null);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    const visibleRows = useMemo(
        () => sortPaymentsByRecency(filterPayments(applyPaymentStat(rows, stat, today), filters)),
        [rows, stat, today, filters],
    );
    const stats = useMemo(() => computePaymentStats(rows, today), [rows, today]);
    const totals = useMemo(() => computePaymentTotals(rows, today), [rows, today]);

    // Sin elección propia se abre el pago más reciente de la lista, como el mock al cargar.
    const selectedPaymentId = pickedPaymentId ?? visibleRows.at(0)?.paymentId ?? null;
    const selectedRow = visibleRows.find((row) => row.paymentId === selectedPaymentId) ?? null;

    return (
        <MockBoundary className={screenFrameClass('fill')}>
            <PaymentsTotalsBand totals={totals} />
            <MasterDetailExplorer
                storageKey="collections-payments"
                filtersCollapsed={filtersCollapsed}
                filters={
                    <PaymentsFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        today={today}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((prev) => !prev);
                        }}
                    />
                }
                list={
                    <>
                        <StatsBar
                            items={paymentsStatsItems(stats)}
                            active={stat}
                            onChange={setStat}
                            ariaLabel="Pagos por periodo"
                        />
                        <PaymentsTable
                            rows={visibleRows}
                            selectedPaymentId={selectedPaymentId}
                            onSelect={setPickedPaymentId}
                        />
                    </>
                }
                detail={<PaymentDetail row={selectedRow} />}
            />
        </MockBoundary>
    );
}
